import { Preferences } from '@capacitor/preferences';
import { ONE_YEAR_MS } from '@shared/hooks/useKeycloakAuth/constants/expiration-durations';

export interface StoredTokens {
  accessToken: string;
  refreshToken: string;
  idToken?: string;
  tokenParsed?: unknown;
  accessTokenExpiry: number; // When the access token expires
  refreshTokenExpiry: number; // When the refresh token expires
  storageExpiry: number; // When to remove from storage entirely
}

export interface StoredUserData {
  user: unknown;
  userPreferences: unknown;
  inboxStatus: { hasUnread: boolean };
  lastUpdated: number;
}

interface StoredAuthState {
  isAuthenticated: boolean;
  userType: string | null;
  tokens: StoredTokens;
  userData: StoredUserData;
}

const STORAGE_KEYS = {
  AUTH_STATE: 'efka_auth_state',
  TOKENS: 'efka_tokens',
  USER_DATA: 'efka_user_data'
} as const;

const setItem = async (key: string, value: string): Promise<void> => {
  await Preferences.set({ key, value });
};

const getItem = async (key: string): Promise<string | null> => {
  const result = await Preferences.get({ key });
  return result.value;
};

const removeItem = async (key: string): Promise<void> => {
  await Preferences.remove({ key });
};

const clearAll = async (): Promise<void> => {
  await Preferences.clear();
};

const storeTokens = async (tokens: StoredTokens): Promise<void> => {
  try {
    const tokenData = JSON.stringify(tokens);
    await setItem(STORAGE_KEYS.TOKENS, tokenData);
  } catch (error) {
    console.error('[Storage] Failed to store tokens:', error);
    throw error;
  }
};

const getStoredTokens = async (): Promise<StoredTokens | null> => {
  try {
    const tokenData = await getItem(STORAGE_KEYS.TOKENS);

    if (!tokenData) return null;

    const tokens: StoredTokens = JSON.parse(tokenData);

    // Check if storage is expired (remove everything)
    if (Date.now() > tokens.storageExpiry) {
      await removeStoredTokens();
      return null;
    }

    // Check if refresh token is expired (remove everything, need to re-login)
    if (Date.now() > tokens.refreshTokenExpiry) {
      await removeStoredTokens();
      return null;
    }

    return tokens;
  } catch (error) {
    console.error('[Storage] Failed to retrieve tokens:', error);
    await removeStoredTokens(); // Clean up corrupted data
    return null;
  }
};

const removeStoredTokens = async (): Promise<void> => {
  try {
    await removeItem(STORAGE_KEYS.TOKENS);
  } catch (error) {
    console.error('[Storage] Failed to remove tokens:', error);
  }
};

const storeUserData = async (userData: unknown): Promise<void> => {
  try {
    const userDataWithTimestamp = {
      userData,
      lastUpdated: Date.now()
    };
    const data = JSON.stringify(userDataWithTimestamp);
    await setItem(STORAGE_KEYS.USER_DATA, data);
  } catch (error) {
    console.error('[Storage] Failed to store user data:', error);
    throw error;
  }
};

const getStoredUserData = async (): Promise<unknown | null> => {
  try {
    const userData = await getItem(STORAGE_KEYS.USER_DATA);
    if (!userData) return null;

    const data = JSON.parse(userData);

    if (data.lastUpdated && Date.now() - data.lastUpdated > ONE_YEAR_MS) {
      await removeStoredUserData();
      return null;
    }

    return data.userData;
  } catch (error) {
    console.error('[Storage] Failed to retrieve user data:', error);
    await removeStoredUserData(); // Clean up corrupted data
    return null;
  }
};

const removeStoredUserData = async (): Promise<void> => {
  try {
    await removeItem(STORAGE_KEYS.USER_DATA);
  } catch (error) {
    console.error('[Storage] Failed to remove user data:', error);
  }
};

const storeAuthState = async (authState: Partial<StoredAuthState>): Promise<void> => {
  try {
    const data = JSON.stringify(authState);
    await setItem(STORAGE_KEYS.AUTH_STATE, data);
  } catch (error) {
    console.error('[Storage] Failed to store auth state:', error);
    throw error;
  }
};

const getStoredAuthState = async (): Promise<Partial<StoredAuthState> | null> => {
  try {
    const authData = await getItem(STORAGE_KEYS.AUTH_STATE);
    if (!authData) return null;

    return JSON.parse(authData);
  } catch (error) {
    console.error('[Storage] Failed to retrieve auth state:', error);
    await removeStoredAuthState(); // Clean up corrupted data
    return null;
  }
};

const removeStoredAuthState = async (): Promise<void> => {
  try {
    await removeItem(STORAGE_KEYS.AUTH_STATE);
  } catch (error) {
    console.error('[Storage] Failed to remove auth state:', error);
  }
};

const clearAllStoredData = async (): Promise<void> => {
  try {
    await Promise.all([removeStoredTokens(), removeStoredUserData(), removeStoredAuthState()]);
  } catch (error) {
    console.error('[Storage] Failed to clear all stored data:', error);
    // Fallback to clearing everything
    await clearAll();
  }
};

const isTokenExpiringSoon = (expiresAt: number, bufferMinutes: number = 5): boolean => {
  const bufferMs = bufferMinutes * 60 * 1000;
  return Date.now() + bufferMs >= expiresAt;
};

const calculateTokenExpiry = (expiresIn: number): number => {
  return Date.now() + expiresIn * 1000;
};

// Convenience methods for logout flow
const clearTokens = async (): Promise<void> => {
  await removeStoredTokens();
};

const clearUserData = async (): Promise<void> => {
  await removeStoredUserData();
};

export const storageService = {
  storeTokens,
  getStoredTokens,
  removeStoredTokens,
  storeUserData,
  getStoredUserData,
  removeStoredUserData,
  storeAuthState,
  getStoredAuthState,
  removeStoredAuthState,
  clearAllStoredData,
  isTokenExpiringSoon,
  calculateTokenExpiry,
  clearTokens,
  clearUserData
};
